import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

function getDialogFocusables(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll("button, input")).filter(
    (node) => !node.disabled,
  );
}

function getDeleteCopy(job) {
  const name = job?.jobName || "this job";
  if (job?.status === "completed") {
    return {
      title: `Delete ${name}?`,
      body: "This completed job and its imported records will be permanently deleted.",
    };
  }
  return {
    title: `Delete ${name}?`,
    body: "This draft and its extracted rows will be permanently deleted.",
  };
}

export default function MaintenanceImportJobDeleteDialog({
  open,
  job,
  busy,
  colors,
  title,
  body,
  confirmPhrase,
  confirmLabel = "Delete",
  confirmBusyLabel = "Deleting...",
  confirmTone = "danger",
  onCancel,
  onConfirm,
}) {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const [confirmValue, setConfirmValue] = useState("");
  const visible = Boolean(open && job);
  const requiresConfirm = Boolean(confirmPhrase);
  const confirmMatched =
    !requiresConfirm ||
    confirmValue.trim() === String(confirmPhrase || "").trim();
  const disabled = Boolean(busy);
  const deleteDisabled = disabled || !confirmMatched;
  const defaults = getDeleteCopy(job);
  const copy = {
    title: title || defaults.title,
    body: body || defaults.body,
  };

  useEffect(() => {
    if (!visible) return undefined;
    setConfirmValue("");
    previouslyFocusedRef.current = document.activeElement;
    const frame = window.requestAnimationFrame(() => {
      cancelRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function" && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!busy) onCancel?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = getDialogFocusables(dialogRef.current);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !dialogRef.current?.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, busy, onCancel]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center px-4">
      <button
        type="button"
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
        aria-label="Cancel"
        disabled={disabled}
        onClick={disabled ? undefined : onCancel}
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-job-delete-title"
        aria-describedby="maintenance-job-delete-body"
        className="relative w-full max-w-[460px] rounded-2xl border p-6 shadow-xl"
        style={{
          backgroundColor: colors.inputBg,
          borderColor: colors.border,
          color: colors.text,
        }}
      >
        <h2 id="maintenance-job-delete-title" className="text-lg font-semibold">
          {copy.title}
        </h2>
        <p
          id="maintenance-job-delete-body"
          className="mt-2 text-sm"
          style={{ color: colors.textSecondary }}
        >
          {copy.body}
        </p>
        {requiresConfirm ? (
          <label className="mt-4 block text-sm" style={{ color: colors.text }}>
            <span style={{ color: colors.textSecondary }}>
              Type <span className="font-semibold">{confirmPhrase}</span> to
              confirm
            </span>
            <input
              type="text"
              value={confirmValue}
              onChange={(event) => setConfirmValue(event.target.value)}
              disabled={disabled}
              autoComplete="off"
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60"
              style={{
                backgroundColor: colors.background || colors.inputBg,
                borderColor: colors.border,
                color: colors.text,
              }}
            />
          </label>
        ) : null}
        <div className="mt-6 flex flex-col-reverse gap-3 min-[480px]:flex-row min-[480px]:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="inline-flex w-full min-[480px]:w-auto shrink-0 items-center justify-center rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: "transparent",
              borderColor: colors.border,
              color: colors.text,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleteDisabled}
            className="inline-flex w-full min-[480px]:w-auto shrink-0 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            style={
              confirmTone === "primary"
                ? {
                    backgroundImage: colors?.gradient,
                    borderColor: "transparent",
                    color: "#ffffff",
                  }
                : {
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444",
                    color: "#ffffff",
                  }
            }
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {confirmBusyLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
